// Radial sweep — one revolution per cycle. Driven directly from the
// rAF now-ref so the hand moves at 60fps, regardless of the parent's
// data-poll rate.
//
// Animation runs off the shared rAF ticker rather than a per-instance
// requestAnimationFrame. The TransportDeck phasor shares a single
// browser frame dispatch with every other ticker subscriber.

import { memo, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { subscribeTick } from '../../strudel/sharedTicker';

type Props = {
  nowRef: MutableRefObject<number>;
  active: boolean;
  size?: number;
};

export const CyclePhasor = memo(CyclePhasorInner);

function CyclePhasorInner({ nowRef, active, size = 56 }: Props) {
  const handRef = useRef<SVGLineElement>(null);
  const arcRef = useRef<SVGPathElement>(null);

  const stroke = active ? '#fde047' : '#52525b';
  const center = size / 2;
  const radius = center - 3;

  useEffect(() => {
    return subscribeTick(() => {
      const now = nowRef.current;
      const frac = ((now % 1) + 1) % 1;
      const angle = frac * 2 * Math.PI - Math.PI / 2;
      const hx = center + Math.cos(angle) * radius;
      const hy = center + Math.sin(angle) * radius;
      const hand = handRef.current;
      if (hand) {
        hand.setAttribute('x2', String(hx));
        hand.setAttribute('y2', String(hy));
      }
      const arc = arcRef.current;
      if (arc) {
        if (active && frac > 0.001) {
          arc.setAttribute('d', describeArc(center, center, radius, 0, frac * 360));
          arc.style.display = '';
        } else {
          arc.style.display = 'none';
        }
      }
    });
  }, [nowRef, center, radius, active]);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label="Cycle phasor"
      role="img"
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#27272a"
        strokeWidth={1}
      />
      {/* Quadrant tick marks. */}
      {[0, 0.25, 0.5, 0.75].map((t) => {
        const a = t * 2 * Math.PI - Math.PI / 2;
        const inner = radius - 3;
        return (
          <line
            key={t}
            x1={center + Math.cos(a) * inner}
            y1={center + Math.sin(a) * inner}
            x2={center + Math.cos(a) * radius}
            y2={center + Math.sin(a) * radius}
            stroke="#3f3f46"
            strokeWidth={1}
          />
        );
      })}
      <path
        ref={arcRef}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        opacity={0.65}
      />
      <line
        ref={handRef}
        x1={center}
        y1={center}
        x2={center}
        y2={center - radius}
        stroke={stroke}
        strokeWidth={1.25}
        strokeLinecap="round"
      />
      <circle cx={center} cy={center} r={1.75} fill={stroke} />
    </svg>
  );
}

function describeArc(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
): string {
  const rad = (d: number) => ((d - 90) * Math.PI) / 180;
  const sx = cx + r * Math.cos(rad(startDeg));
  const sy = cy + r * Math.sin(rad(startDeg));
  const ex = cx + r * Math.cos(rad(endDeg));
  const ey = cy + r * Math.sin(rad(endDeg));
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
}
